import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { Button, Col, Row } from "react-bootstrap";
import moment from "moment";
import momentTz from "moment-timezone";
import { CountdownCircleTimer } from "react-countdown-circle-timer";

import { API } from "../utils/api";
import {
  getBookingStatusLabel,
  getFilterTime,
  showToast,
} from "../utils/helper";
import ExecutePayment from "./Component/ExecutePayment";
import { GOOGLE_API_KEY } from "../utils/env";
import {
  compactVehicleImg,
  standardVehicleImg,
  suvVehicleImg,
} from "../Constants/images";
import { useSelector } from "react-redux";
import { trackEvent } from "../utils/analytics";

const getCheckInLogTime = (log) => {
  if (!log) return null;
  if (log.userCheckin && log.userCheckin.checkin && log.userCheckin.dateTime) {
    return log.userCheckin.dateTime;
  }
  if (log.hostCheckin && log.hostCheckin.checkin && log.hostCheckin.dateTime) {
    return log.hostCheckin.dateTime;
  }
  return null;
};

const getCheckOutLogTime = (log) => {
  if (!log) return null;
  if (
    log.hostCheckout &&
    log.hostCheckout.checkout &&
    log.hostCheckout.dateTime
  ) {
    return log.hostCheckout.dateTime;
  }
  if (
    log.userCheckout &&
    log.userCheckout.checkout &&
    log.userCheckout.dateTime
  ) {
    return log.userCheckout.dateTime;
  }
  return null;
};

const isCheckedInFromLog = (log) =>
  Boolean(
    log &&
      ((log.userCheckin && log.userCheckin.checkin) ||
        (log.hostCheckin && log.hostCheckin.checkin)),
  );

const isCheckedOutFromLog = (log) =>
  Boolean(
    log &&
      ((log.hostCheckout && log.hostCheckout.checkout) ||
        (log.userCheckout && log.userCheckout.checkout)),
  );

const BookingDetails = ({ title, item, onClose = null, onUpdated = null }) => {
  const params = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const bookingId = params.bookingId ? params.bookingId : "";

  const [booking, setBookingData] = useState(item || {});
  const [spot, setSpotData] = useState((item && item.spot) || {});
  const [loading, setLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [initialRemaining, setInitialRemaining] = useState(0);
  const [duration, setDuration] = useState(0);
  const [timerSeed, setTimerSeed] = useState(0);
  const [timerStarted, setTimerStarted] = useState(false);
  const [isTimeReached, setIsTimeReached] = useState(false);
  const [isEndDatePassed, setIsEndDatePassed] = useState(false);
  const [startDateFormatted, setStartDateFormatted] = useState("");
  const [endDateFormatted, setEndDateFormatted] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [checkOutTime, setCheckOutTime] = useState("");
  const [payNowLoading, setPayNowLoading] = useState(false);
  const [paymentModal, setPaymentModal] = useState({
    show: false,
    paymentInfo: null,
    bookingInfo: null,
  });

  const isCheckedIn = isCheckedInFromLog(booking.log);
  const isCheckedOut = isCheckedOutFromLog(booking.log);

  const mapRef = useRef(null);
  const [spotMarker, setSpotMarker] = useState({
    lat: 38.905778525393636,
    lng: -77.01938252900719,
  });
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: ["places"],
  });

  const getBookingDetails = () => {
    const idToFetch = bookingId || (item && item.id);
    if (!idToFetch) return;

    API.getSingleBooking(idToFetch)
      .then((res) => {
        if (res.type === "success" && res.data) {
          setBookingData(res.data);
          setSpotData(res.data.spot || {});

          if (
            res.data.spot &&
            res.data.spot.location &&
            res.data.spot.location.coordinates
          ) {
            setSpotMarker({
              lat: res.data.spot.location.coordinates[1],
              lng: res.data.spot.location.coordinates[0],
            });
          }
        }
      })
      .catch((err) => {
        showToast("error", err.message || "Could not load booking");
      });
  };

  const recomputeTimer = useCallback(() => {
    if (!booking || !booking.startDate || !spot) return;

    const timeZone = spot.timeZone || momentTz.tz.guess();
    const currentDate = momentTz.tz(timeZone);
    const startDate = momentTz.tz(
      `${booking.startDate} ${booking.startTime}`,
      timeZone,
    );
    const endDate = momentTz.tz(
      `${booking.endDate} ${booking.endTime}`,
      timeZone,
    );

    setStartDateFormatted(startDate.format("MM/DD/YYYY hh:mm A"));
    setEndDateFormatted(endDate.format("MM/DD/YYYY hh:mm A"));

    const fullDurationSec = Math.max(0, (endDate - startDate) / 1000);
    setDuration(fullDurationSec);

    let seconds = 0;
    let started = false;

    if (startDate <= currentDate && currentDate < endDate) {
      seconds = (endDate - currentDate) / 1000;
      setIsTimeReached(true);
      started = true;
    } else {
      seconds = fullDurationSec;
    }

    if (isCheckedOutFromLog(booking.log)) {
      started = false;
    }

    if (endDate < currentDate) {
      setIsEndDatePassed(true);
      setIsTimeReached(true);
    } else {
      setIsEndDatePassed(false);
    }

    const finalSeconds = seconds > 0 ? seconds : 0;
    setRemainingTime(finalSeconds);
    setInitialRemaining(finalSeconds);
    setTimerSeed((s) => s + 1);
    setTimerStarted(started);
  }, [booking, spot]);

  useEffect(() => {
    if (item) {
      setBookingData(item);
      setSpotData(item.spot || {});
      if (
        item.spot &&
        item.spot.location &&
        item.spot.location.coordinates
      ) {
        setSpotMarker({
          lat: item.spot.location.coordinates[1],
          lng: item.spot.location.coordinates[0],
        });
      }

      if (item.id) {
        API.getSingleBooking(item.id)
          .then((res) => {
            if (res && res.type === "success" && res.data) {
              setBookingData(res.data);
              setSpotData(res.data.spot || {});
              if (
                res.data.spot &&
                res.data.spot.location &&
                res.data.spot.location.coordinates
              ) {
                setSpotMarker({
                  lat: res.data.spot.location.coordinates[1],
                  lng: res.data.spot.location.coordinates[0],
                });
              }
            }
          })
          .catch(() => {});
      }
    } else {
      if (!isAuthenticated) {
        navigate("/login", {
          state: { redirectUrl: `/booking/${bookingId}` },
        });
      } else {
        getBookingDetails();
      }
    }
  }, []);

  useEffect(() => {
    recomputeTimer();
  }, [recomputeTimer]);

  useEffect(() => {
    const checkin = getCheckInLogTime(booking.log);
    const checkout = getCheckOutLogTime(booking.log);
    setCheckInTime(
      checkin ? moment(checkin).format("MM/DD/YYYY hh:mm a") : "",
    );
    setCheckOutTime(
      checkout ? moment(checkout).format("MM/DD/YYYY hh:mm a") : "",
    );
  }, [booking.log]);

  const handleCheckInOut = () => {
    if (!isTimeReached || loading) return;
    if (!spot || !spot.location || !spot.location.coordinates) {
      showToast("error", "Spot location unavailable");
      return;
    }

    const payload = {
      bookingId: booking.id,
      location: {
        lat: spot.location.coordinates[1],
        lng: spot.location.coordinates[0],
      },
    };

    const isHost = user && user.id === booking.hostId;
    const action = !isCheckedIn
      ? isHost
        ? API.hostVehicleCheckIn
        : API.vehicleCheckIn
      : isHost
        ? API.hostVehicleCheckOut
        : API.vehicleCheckOut;

    setLoading(true);
    action(payload)
      .then((res) => {
        if (res && res.type === "success") {
          showToast("success", res.message);
          const updatedLog = res.data;

          // Apply the log immediately so the panel UI flips to checked-in/out.
          setBookingData((prev) => ({
            ...prev,
            log: updatedLog || prev.log,
          }));

          if (!isCheckedIn) {
            trackEvent(
              isHost ? "web_host_check_in" : "web_user_check_in",
              { booking_id: booking.id },
            );
          } else {
            trackEvent(
              isHost ? "web_host_check_out" : "web_user_check_out",
              { booking_id: booking.id },
            );
          }

          // Refetch the booking so the new `status` (e.g. "completed" after
          // check-out) is broadcast to the list. The check-in/out endpoint
          // only returns the log row, so without this round-trip the list
          // keeps showing the stale StatusDot until a page refresh.
          API.getSingleBooking(booking.id)
            .then((res2) => {
              if (res2 && res2.type === "success" && res2.data) {
                setBookingData((prev) => ({
                  ...prev,
                  ...res2.data,
                }));
                if (typeof onUpdated === "function") onUpdated(res2.data);
              }
            })
            .catch(() => {})
            .finally(() => {
              if (isCheckedIn && onClose) setTimeout(onClose, 500);
            });
        } else {
          showToast("error", (res && res.message) || "Action failed");
        }
      })
      .catch((err) => {
        showToast("error", (err && err.message) || "Action failed");
      })
      .finally(() => setLoading(false));
  };

  const handlePayNow = () => {
    if (payNowLoading) return;
    const paymentId = booking.payment && booking.payment.id;
    if (!paymentId) {
      showToast("error", "Payment record missing for this booking");
      return;
    }

    setPayNowLoading(true);
    API.refreshPaymentIntent({ bookingId: booking.id, paymentId })
      .then(async (res) => {
        const payment = res && res.data && res.data.payment;
        if (!res || res.type !== "success" || !payment || !payment.clientSecret) {
          showToast(
            "error",
            (res && res.message) || "Could not start payment",
          );
          return;
        }

        let bookingFromRes = (res.data && res.data.booking) || booking;
        try {
          const fresh = await API.getSingleBooking(booking.id);
          if (fresh && fresh.type === "success" && fresh.data) {
            bookingFromRes = fresh.data;
            setBookingData(fresh.data);
          }
        } catch (e) {
          // fall through with the booking we have
        }

        const bp = bookingFromRes.payment || {};

        setPaymentModal({
          show: true,
          bookingInfo: bookingFromRes,
          paymentInfo: {
            ...payment,
            paymentId: payment.paymentId || paymentId,
            grossAmount:
              payment.grossAmount || bp.grossAmount || bookingFromRes.grossAmount,
            stripeFee: payment.stripeFee || bp.stripeFee || 0,
            amount:
              payment.amount || bp.totalAmount || bookingFromRes.grossAmount,
          },
        });
      })
      .catch((err) =>
        showToast("error", (err && err.message) || "Could not start payment"),
      )
      .finally(() => setPayNowLoading(false));
  };

  const vehicleImg =
    booking && booking.vehicle && booking.vehicle.type === "standard"
      ? standardVehicleImg
      : booking && booking.vehicle && booking.vehicle.type === "compact"
        ? compactVehicleImg
        : booking && booking.vehicle && booking.vehicle.type === "suv"
          ? suvVehicleImg
          : "";

  const homeLink =
    isAuthenticated && user.type === "admin" ? "/admin/spots" : "user/spots";

  const statusLabel = getBookingStatusLabel(booking.status);

  let leaveBeforeLabel = "";
  if (isCheckedOut) {
    leaveBeforeLabel = `Left at\n${checkOutTime || endDateFormatted}`;
  } else if (isCheckedIn) {
    leaveBeforeLabel = `Leave before\n${endDateFormatted}`;
  } else {
    leaveBeforeLabel = `Arriving at\n${startDateFormatted}`;
  }

  const status = booking.status;
  const isPaymentPending = status === "payment-pending";
  const showCheckInOut =
    (status === "accepted" || status === "paid") && !isCheckedOut;

  let actionButtonLabel = "Check In";
  if (isCheckedIn && !isCheckedOut) actionButtonLabel = "Check Out";
  if (isEndDatePassed && !isCheckedIn) actionButtonLabel = "Parking Time Expired";

  const actionButtonDisabled =
    loading ||
    (!isTimeReached && !isEndDatePassed) ||
    (isEndDatePassed && !isCheckedIn);

  return (
    <div className="spot-details-container">
      <div className="map-container">
        <div className="container">
          <div className="map-heading">
            {title && <h4>{title}</h4>}

            {!title && (
              <span
                className="home-icon asLink"
                onClick={() => navigate(homeLink)}
              >
                <i className="fas fa-home" />
              </span>
            )}

            {onClose && (
              <span className="home-icon asLink" onClick={onClose}>
                <i className="fas fa-times" />
              </span>
            )}
          </div>

          <div className="map-wrapper">
            {isLoaded && spot && (
              <GoogleMap
                ref={mapRef}
                mapContainerStyle={{ width: "100%", height: 250 }}
                center={spotMarker}
                zoom={12}
              >
                {spotMarker && spotMarker.lat !== 0 && (
                  <Marker position={spotMarker} />
                )}
              </GoogleMap>
            )}
          </div>
        </div>
      </div>

      <div className="container">
        {!booking.id ? (
          <div className="text-center py-5">
            <i
              className="fas fa-spin fa-spinner"
              style={{ fontSize: 28, color: "#9a9a9f" }}
            />
            <p className="text-muted mt-3 mb-0">Loading booking details…</p>
          </div>
        ) : (
        <div className="spot-details">
          <div className="spot-timer-wrapper">
            <CountdownCircleTimer
              key={timerSeed}
              trailColor="#D9D9D9"
              duration={Math.max(duration, 1)}
              initialRemainingTime={initialRemaining}
              size={132}
              strokeWidth={17}
              isPlaying={timerStarted && !isCheckedOut}
              colors={["#00FFA6", "#000000", "#510520"]}
              colorsTime={[duration, duration / 2, 0]}
              onUpdate={(time) => {
                if (time >= 0) setRemainingTime(time);
              }}
            >
              {() =>
                vehicleImg ? (
                  <img
                    src={vehicleImg}
                    alt="vehicle"
                    style={{ width: 63, height: 25, objectFit: "contain" }}
                  />
                ) : null
              }
            </CountdownCircleTimer>

            <div className="timer-details">
              <div className="remaining-time">
                {remainingTime > 0
                  ? getFilterTime(remainingTime)
                  : "00:00:00"}
              </div>
              <div
                className="leave-time"
                style={{ whiteSpace: "pre-line" }}
              >
                {leaveBeforeLabel}
              </div>
              {booking.vehicle && booking.vehicle.licensePlate && (
                <div className="vehicle-text">
                  {booking.vehicle.licensePlate}
                </div>
              )}
            </div>
          </div>

          <div className="spot-details-wrapper">
            <div className="spot-heading-w">
              <span className="spot-title">{spot.name}</span>
              <Row>
                <Col>
                  <p className="mb-1">
                    {startDateFormatted}
                    <br />
                    {endDateFormatted}
                  </p>
                </Col>
                <Col className="text-right">
                  <p>
                    <b>
                      $
                      {booking.payment && booking.payment.totalAmount
                        ? booking.payment.totalAmount
                        : booking.grossAmount}
                    </b>
                  </p>
                </Col>
              </Row>
              {statusLabel && (
                <p className="text-muted text-capitalize mb-0">
                  Status: {statusLabel}
                </p>
              )}
            </div>

            <div>
              <span className="spot-title">Address</span>
              <p>{spot.address}</p>
            </div>

            {isCheckedIn && checkInTime && (
              <p className="text-muted mb-0">Checked in at {checkInTime}</p>
            )}
            {isCheckedOut && checkOutTime && (
              <p className="text-muted mb-0">Checked out at {checkOutTime}</p>
            )}
          </div>

          {showCheckInOut && (
            <div className="button-wrapper mt-3">
              <Button
                className="btn-primary"
                disabled={actionButtonDisabled}
                onClick={handleCheckInOut}
              >
                <span>
                  {loading && <i className="fas fa-spin fa-spinner me-2" />}
                  {!loading && actionButtonLabel}
                </span>
              </Button>
            </div>
          )}

          {isPaymentPending && (
            <div className="button-wrapper mt-3">
              <Button
                className="btn-primary"
                disabled={payNowLoading}
                onClick={handlePayNow}
              >
                <span>
                  {payNowLoading && (
                    <i className="fas fa-spin fa-spinner me-2" />
                  )}
                  {!payNowLoading && "Pay Now"}
                </span>
              </Button>
            </div>
          )}
        </div>
        )}
      </div>

      {paymentModal.show && paymentModal.paymentInfo && (
        <ExecutePayment
          show={paymentModal.show}
          bookingInfo={paymentModal.bookingInfo}
          paymentInfo={paymentModal.paymentInfo}
          onClose={() =>
            setPaymentModal({
              show: false,
              paymentInfo: null,
              bookingInfo: null,
            })
          }
          resetForm={() => {}}
          onSuccess={() => {
            setPaymentModal({
              show: false,
              paymentInfo: null,
              bookingInfo: null,
            });
            if (booking.id) {
              API.getSingleBooking(booking.id)
                .then((res) => {
                  if (res && res.type === "success" && res.data) {
                    setBookingData(res.data);
                    if (typeof onUpdated === "function") onUpdated(res.data);
                  }
                })
                .catch(() => {});
            }
          }}
        />
      )}
    </div>
  );
};

export default BookingDetails;
